import axios from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  Domain,
  DomainIdInput,
  DomainInput,
  DomainVerification,
  UpdateDomainInput,
} from './domains.types'

export interface DomainOrganizationScope {
  organizationId: OrganizationId
}

/** Scope for reading one domain; the Laravel route requires its id. */
export interface DomainScope extends DomainOrganizationScope {
  domainId: DomainIdInput
}

/** The show query key's entries; the shared key builder can't mark an entry required. */
export interface DomainShowKeyEntries extends DomainOrganizationScope {
  domainId?: DomainIdInput
}

/** Calls that change one domain. */
interface DomainTarget extends DomainOrganizationScope {
  domainId: DomainIdInput
}

/** Query functions read only the key. */
type DomainQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'>

/** `DomainController::index()` lists every domain of the organization and reads no filters. */
export async function getDomains({
  queryKey: [{ organizationId }],
}: DomainQueryContext<DomainOrganizationScope>) {
  return (await axios.get<Domain[]>('/domains', buildAdminRequestConfig({ organizationId }))) || []
}

export async function getDomain({
  queryKey: [{ organizationId, domainId }],
}: DomainQueryContext<DomainShowKeyEntries>) {
  const response = await axios.get<Domain>(
    `/domains/${domainId}`,
    buildAdminRequestConfig({ organizationId })
  )
  return response || {}
}

export async function createDomain({
  organizationId,
  ...values
}: DomainInput & DomainOrganizationScope) {
  return axios.post<Domain>('/domains', values, buildAdminRequestConfig({ organizationId }))
}

export async function updateDomain({
  organizationId,
  id,
  ...values
}: UpdateDomainInput & DomainOrganizationScope) {
  return axios.put<Domain>(`/domains/${id}`, values, buildAdminRequestConfig({ organizationId }))
}

export async function deleteDomain({ organizationId, domainId }: DomainTarget) {
  return axios.delete<void>(`/domains/${domainId}`, buildAdminRequestConfig({ organizationId }))
}

export async function verifyDomain({ organizationId, domainId }: DomainTarget) {
  return axios.post<DomainVerification>(
    `/domains/verify/${domainId}`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function registerStripePaymentMethodDomain({
  organizationId,
  domainId,
}: DomainTarget) {
  return axios.post<Domain>(
    `/domains/${domainId}/stripe-payment-method-domain`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}
